import { h, Fragment } from "preact";
import useFormStore from "../../store/useFormStore";
import {
  formatCurrency
} from "./cartHelpers";

export default function CartBlock({ showServiceItems = true}) {
  const cart = useFormStore((state) => state.cart);
  const currentFranchise = useFormStore((state) => state.currentFranchise);
  const needsPortableEquipment = useFormStore((state) => state.needsPortableEquipment);
  const getServiceById = useFormStore((state) => state.getServiceById);

  // Check if this franchise uses pricing
  const usesPricing = currentFranchise?.uses_pricing !== false; // Default to true if undefined

  // Service charge from cart state
  const serviceCharge = cart.serviceCharge || 0;

  // Portable equipment charge
  const portableCharge = parseFloat(currentFranchise?.prices?.field_portable_charge || 0);

  // Get category summary (reactive) - direct state access
  const categorySummary = useFormStore((state) => state.cart.categorySummary);

  // Get tax rate from cart state (already set during cart initialization)
  const taxRate = parseFloat(cart.taxRate || 0).toFixed(2);

  // Filter out Service Charge and Equipment items to check if cart has actual services
  const serviceItems = cart.items?.filter(
    item => item.category !== "Service Charge" && item.category !== "Equipment"
  ) || [];

  if (!cart.items || cart.items.length === 0 || serviceItems.length === 0) {
    return (
      <div className="bg-white p-6 shadow-sm">
        <div className="text-2xl font-bold mb-4">Requested Services</div>
        <p className="font-bold">Select some cleaning options to get started.</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 shadow-sm">
      <div className="text-2xl font-bold mb-6">{usesPricing ? 'Your Estimate' : 'Requested Services'}</div>

      {/* Debug help */}
      {process.env.NODE_ENV === 'development' && (
        <>
          <details>
            <summary className="text-xs">Debug: Cart Category Summary json data</summary>
            <div className="mt-2 text-xs bg-gray-100 p-4 rounded overflow-auto max-h-96">
              <pre>{JSON.stringify(categorySummary, null, 1)}</pre>
            </div>
          </details>
          <details>
            <summary className="text-xs">Debug: Full Cart json data</summary>
            <div className="mt-2 text-xs bg-gray-100 p-4 rounded overflow-auto max-h-96">
              <pre>{JSON.stringify(cart, null, 2)}</pre>
            </div>
          </details>
        </>
      )}

      {/* Service Items - Grouped by Category */}
      {showServiceItems && (
        <div className="flex flex-col gap-2 mb-6">
          {(() => {
            // Filter out Service Charge and Equipment categories
            const filteredCategories = Object.entries(categorySummary || {})
              .filter(([categoryName]) =>
                categoryName !== "Service Charge" &&
                categoryName !== "Equipment"
              );

            // Render categories
            return filteredCategories.map(([categoryName, categoryData]) => {
            // Get service definition from first item's service_term
            const firstItem = categoryData.items?.[0];
            const serviceTerm = firstItem?.service_term || firstItem?.category_id;
            // Get service definition from franchise data
            const serviceDefinition = serviceTerm ? getServiceById(serviceTerm) : null;

            // Format quantity with proper label using Drupal.formatPlural
            const quantityText = Drupal.formatPlural(
              categoryData.quantity ?? 0,
              serviceDefinition?.labels?.singular ?? "@count item",
              serviceDefinition?.labels?.plural ?? "@count items"
            );

            // Get addons list
            const addonsList = Object.values(categoryData.addons || {});

            return (
              <div key={categoryName} className="service-category flex flex-col gap-1">
                {/* Category Header Row */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="font-bold col-span-2">
                    {categoryName}
                  </div>
                  <div className="text-right">
                    {quantityText}
                  </div>
                  {usesPricing && (
                    <div className="text-right">
                      {formatCurrency(categoryData.subtotal)}
                    </div>
                  )}
                </div>

                {/* Addon Rows */}
                {addonsList.length > 0 && (
                  <div className="text-base">
                    {addonsList.map((addon) => {
                      // Format addon quantity using same labels as parent service
                      const addonQuantityText = Drupal.formatPlural(
                        addon.quantity ?? 0,
                        serviceDefinition?.labels?.singular ?? "@count item",
                        serviceDefinition?.labels?.plural ?? "@count items"
                      );

                      return (
                        <div key={addon.text} className="grid grid-cols-4 gap-4">
                          <div className="col-span-2 pl-6">
                            {addon.text}
                          </div>
                          <div className="text-right">
                            {addonQuantityText}
                          </div>
                          {usesPricing && (
                            <div className="text-right">
                              {formatCurrency(addon.subtotal)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          });
        })()}
        </div>
      )}

      {/* Discounts Section */}
      {(() => {
        // Use appliedDiscounts for pricing franchises, discounts for non-pricing franchises
        const discountsToShow = usesPricing ? cart.appliedDiscounts : cart.discounts;

        if (!discountsToShow || discountsToShow.length === 0) {
          return null;
        }

        return (
          <div className="cart__discounts border-t border-gray-300 pt-4 mt-6">
            <div className="text-xl font-bold mb-3">Discounts</div>
            <div className="flex flex-col gap-2">
              {discountsToShow.map((discount) => {
                // For appliedDiscounts, get full discount info from cart.discounts
                // For cart.discounts, use the discount directly
                const promoData = currentFranchise?.promotions?.find(
                  (promo) => promo.promo_code === discount.code || promo.promo_code === discount.promo_code
                );
                const discountTitle = discount.title || discount.text || discount.promo_code || `${discount.amount}% off`;
                const discountDescription = discount.description || promoData?.description;

                return (
                  <div key={discount.id || discount.promo_code || discount.code} className="flex justify-between items-start cart__discounts--item">
                    <div>
                      <div className="font-semibold">
                        {discountTitle}
                      </div>
                      {discountDescription && (
                        <div className="text-sm text-gray-600">
                          {discountDescription}
                        </div>
                      )}
                    </div>
                    {usesPricing && (
                      <div className="font-semibold text-lg text-coit-success">
                        -{formatCurrency(discount.discount_amount || discount.savings)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Pricing sections - only show when uses_pricing is true */}
      {usesPricing && (
        <>
          {/* Other Charges - below discounts with divider */}
          <div className="cart__pricing-section border-t border-gray-300 pt-4 mt-6">
            <div className="flex flex-col gap-2 mb-6">

              {/* Minimum Amount */}
              {!cart.minimumAmountMet && (
                <div className="cart__minimum-amount flex justify-between items-center">
                  <div className="font-semibold text-lg">Minimum Job Amount</div>
                  <div className="font-semibold text-lg">
                    {formatCurrency(cart.minimumAmountNeeded)}
                  </div>
                </div>
              )}

              {/* Portable Equipment Charge */}
              {needsPortableEquipment && portableCharge > 0 && (
                <div className="cart__portable-equipment-charge flex justify-between items-center">
                  <div className="font-semibold text-lg">Portable Equipment Charge</div>
                  <div className="font-semibold text-lg">
                    {formatCurrency(portableCharge)}
                  </div>
                </div>
              )}

              {/* Tax */}
              {cart.taxes > 0 && (
                <div className="cart__taxes flex justify-between items-center">
                  <div className="font-semibold text-lg">
                    Tax ({taxRate}%)
                  </div>
                  <div className="font-semibold text-lg">
                    {formatCurrency(cart.taxes)}
                  </div>
                </div>
              )}

              {/* Service Charge */}
              <div>
                <div className="flex justify-between items-center">
                  <div className="font-semibold text-lg">Service Charge</div>
                  <div className="font-semibold text-lg">
                    {formatCurrency(serviceCharge)}
                  </div>
                </div>
                {currentFranchise?.prices?.field_additional_charge_disclaimer && (
                  <div className="mt-1 text-sm text-gray-600">
                    {currentFranchise.prices.field_additional_charge_disclaimer}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Estimated Total */}
          <div className="cart__estimated-total-section flex justify-between items-center py-4 border-t border-gray-300 mt-4">
            <div className="font-bold text-xl">Estimated Total*</div>
            <div className="font-bold text-xl">
              {formatCurrency(cart.total)}
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-2 text-sm text-gray-700 leading-relaxed">
            * This is an estimate only. The price may vary once the technician completes a thorough inspection.
          </div>
        </>
      )}
    </div>
  );
}
